import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { getPropertiesWithInvalidCoordinates } from "@/shared/utils/coordinateValidation";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldModal } from "@emerald-react/modal";
import { selectProperties } from "@survey/surveySelectors";
import { setOpenPropertiesDrawer } from "@survey/surveySlice";
import { useEffect, useEffectEvent, useRef, useState } from "react";

/**
 * InvalidCoordinatesModal displays a warning modal when properties have invalid latitude/longitude data
 * Gets properties from Redux store using surveySelectors to avoid prop drilling
 */
const InvalidCoordinatesModal = () => {
  const dispatch = useAppDispatch();
  const properties = useAppSelector(selectProperties);
  const hasShownModalRef = useRef(false);
  const [warningError, setWarningError] = useState(false);

  // Derive invalid properties - React Compiler will memoize this automatically
  const invalidProperties = getPropertiesWithInvalidCoordinates(properties);

  // Effect Event to show warning modal - doesn't need to be in dependency array
  const showWarningModal = useEffectEvent(() => {
    hasShownModalRef.current = true;
    setWarningError(true);
  });

  // Open warning modal once when invalid properties are detected
  useEffect(() => {
    if (invalidProperties.length > 0 && !hasShownModalRef.current) {
      showWarningModal();
    }
  }, [invalidProperties.length]);

  const handleClose = () => {
    setWarningError(false);
    dispatch(setOpenPropertiesDrawer(true));
  };

  return (
    <EmeraldModal
      hasCloseIcon
      onClose={handleClose}
      opened={warningError}
      headerText="UPDATE REQUIRED"
      className="badCoordinates-modal"
      zIndex={1000}
    >
      <div className="flex flex-col gap-4 outline-none max-h-[400px] overflow-y-auto">
        <EmeraldIcon
          className="text-warning items-center text-center text-5xl!"
          icon="warning_amber"
          variant="icon"
        />
        {invalidProperties.length > 0 && (
          <>
            <p>
              The following property record(s) have invalid Latitude and
              Longitude data, which is preventing the Market Map from loading
              correctly. Please update the record(s) with valid data:
            </p>{" "}
            <ul className="font-body mb-2 ml-6">
              {" "}
              {invalidProperties.map((property) => (
                <li key={property.propertyId} className="list-disc">
                  {property.name || "Unnamed Property"}{" "}
                  {property.city && property.stateProvince && (
                    <p className="text-secondary-light text-sm">
                      {property.city}, {property.stateProvince}
                    </p>
                  )}{" "}
                </li>
              ))}{" "}
            </ul>
          </>
        )}
      </div>
      <div className="flex justify-end">
        <EmeraldButton label="Resolve" onClick={handleClose} />
      </div>
    </EmeraldModal>
  );
};

export default InvalidCoordinatesModal;
